// PRD 14 I5 (story 49, Q27) — the Tenis10 children of an account on its player page (a deleted account's too): every child
// claimed into it, with its legacy family and what support may do — above all the move of a deleted account's hidden child to
// the other parent's account. Shows nothing for an account without Tenis10 children. The I0 simulation's cp-account-deleted.
// PRD 16 LR16 (LR10 verdict U-B): on the user page the panel is the „Children (n)” tab (`embedded`) and the page holds the read
// (`read`), so the tab's count, the header's and the panel show the same children.
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Card, CardBody } from "reactstrap"
import { getAccountChildren } from "./api"
import ChildrenTable from "./ChildrenTable"
import SupportActionDialog from "./SupportActionDialog"
import { errorText } from "./claimsLabels"
import "../../assets/scss/custom/pages/_tenis10Claims.scss"

/**
 * The account's Tenis10 children: `{ state, reload }` — state `loading`, `ready` (`data`: account + children) or `error`
 * (`message`). Only the latest read answers (Tenis10 Claims lesson 2026-10-17): a Retry pressed twice, a reload after an
 * action, a Retry still on its way when the page moves to another account — an older answer never replaces a newer one.
 */
export function useAccountChildren(accountId) {
  const [state, setState] = useState({ accountId: null, status: "loading" })
  const latest = useRef(0)
  const reload = useCallback(() => {
    if (!accountId) return undefined
    const request = ++latest.current
    const answer = next => { if (latest.current === request) setState(next) }
    getAccountChildren(accountId)
      .then(data => {
        // an answer of another shape is a failed read, never a broken player page
        if (!Array.isArray(data?.children) || !data.account) throw new Error("Invalid Tenis10 children response")
        answer({ accountId, status: "ready", data })
      })
      .catch(error => answer({ accountId, status: "error", message: errorText(error, "The Tenis10 children didn't load.") }))
    // another account, or the page gone: this read no longer answers
    return () => { latest.current += 1 }
  }, [accountId])
  useEffect(reload, [reload])
  // another account's answer is never this account's
  return { state: state.accountId === accountId ? state : { accountId, status: "loading" }, reload }
}

export default function Tenis10ChildrenPanel({ accountId, read, embedded = false }) {
  const own = useAccountChildren(read ? null : accountId)
  const { state, reload: load } = read || own
  const [dialog, setDialog] = useState(null)
  const [banner, setBanner] = useState(null)
  const frame = content => (embedded ? <div className="tenis10-claims">{content}</div> : <Card className="tenis10-claims"><CardBody className="p-0">{content}</CardBody></Card>)
  if (state.status === "loading") return embedded ? <p role="status" className="text-muted mb-0">Loading the Tenis10 children…</p> : null
  // a quiet line: the player's page is about the player; its Tenis10 children are one part of it
  if (state.status === "error") {
    const line = <p role="status" className="text-muted mb-0">{state.message} <button type="button" className="btn btn-link btn-sm p-0" aria-label="Retry the Tenis10 children" onClick={load}>Retry</button></p>
    return embedded ? line : <Card className="tenis10-claims"><CardBody>{line}</CardBody></Card>
  }
  const { account, children } = state.data
  // nothing to show for an account without Tenis10 children — unless an action just took its last one away; a parent's tab says so
  if (!children.length && !banner) return embedded ? <p className="text-muted mb-0">This account has no Tenis10 child yet.</p> : null
  return frame(<>
    <div className="p-3 border-bottom">
      {!embedded && <h4 className="card-title mb-1">Tenis10 children of this account <span className="badge badge-soft-secondary">{children.length}</span></h4>}
      <p className="card-title-desc mb-0">{account.deleted
        ? "The account is deleted: the children claimed into it are hidden with it. On request, move a child into another Sportya account; it becomes visible again there."
        : "Every Tenis10 child of this account — claimed from the Tenis10 migration or added in Sportya. Support can activate a passport, correct a child's data, cancel a claim or move a child."}</p>
    </div>
    {banner && <div className="alert alert-success m-3 py-2 px-3" role="status">{banner} The action and your note are in the activity log.</div>}
    {children.length > 0 ? <ChildrenTable rows={children} showFamily label="Tenis10 children of this account" onAction={(action, child) => setDialog({ action, child })} />
      : <p className="text-muted px-3 pb-3 mb-0">No Tenis10 child is left in this account.</p>}
    {dialog && <SupportActionDialog action={dialog.action} child={dialog.child} onClose={() => setDialog(null)} onDone={message => {
      setDialog(null)
      setBanner(message)
      load()
    }} />}
  </>)
}
